import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { orderService, formatApiError, resolveImageUrl } from "../services/catalogService";
import { trackEvent } from "../analytics/ga4";
import { getAttribution } from "../lib/tracking";
import { FEATURES } from "../config/features";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function OrderRequestPage() {
  const cart = useCart();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  const [data, setData] = useState({
    contact_name: "", email: "", company: "", country: "", business_type: "", message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Hidden from the public site per client decision (config/features.js)
  if (!FEATURES.orderRequests) return <Navigate to="/" replace />;

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setData((d) => ({ ...d, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!data.contact_name.trim()) e.contact_name = t("pages.order.errRequired");
    if (!data.email.trim()) e.email = t("pages.order.errRequired");
    else if (!EMAIL_RE.test(data.email)) e.email = t("pages.order.errEmail");
    if (cart.lines.length === 0) e.lines = t("pages.order.errLines");
    if (cart.lines.some((l) => !l.quantity || l.quantity < 1)) e.lines = t("pages.order.errQty");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await orderService.submit({
        contact_name: data.contact_name,
        email: data.email,
        company: data.company || null,
        country: data.country || null,
        business_type: data.business_type || null,
        message: data.message || null,
        language: lang,
        source_page: window.location.pathname,
        ...getAttribution(),
        lines: cart.lines.map((l) => ({
          variety_id: l.variety_id, variety_name: l.variety_name,
          quantity: Number(l.quantity), unit: l.unit || "__pending__",
        })),
      });
      cart.clear();
      trackEvent("order_request", { items: cart.lines.length }); // Task 20 — GA4 conversion
      navigate(`/order-request/confirmation/${encodeURIComponent(res.reference)}`, { replace: true });
    } catch (err) {
      toast.error(t("pages.order.submitFail"), { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  const fieldCls = "w-full bg-transparent border-b border-black/15 py-2.5 text-[#252525] placeholder-black/30 focus:outline-none focus:border-[#D92A47] transition-colors";

  return (
    <main className="min-h-screen bg-[#F6F2EC] text-[#252525] pt-28 pb-24" data-testid="order-request-page">
      <div className="container-tessa">
        <Link to="/" className="flex w-fit items-center gap-2 text-[12px] uppercase tracking-[0.16em] text-[#817B74] hover:text-[#D92A47] mb-8">
          <ArrowLeft size={14} /> {t("pages.backHome")}
        </Link>
        <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">{t("pages.order.eyebrow")}</span>
        <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] text-[#252525] mt-3 mb-10">{t("pages.order.title")}</h1>

        <div className="rounded-sm border border-[#C4A46B]/50 bg-[#C4A46B]/10 p-4 mb-8 text-[13px] text-[#5f5a54]" data-testid="unit-notice">
          <strong className="font-semibold text-[#252525]">{t("pages.order.unitNoticeTitle")}</strong>{" "}
          {t("pages.order.unitNotice")} (<code>__pending__</code>)
        </div>

        <form onSubmit={onSubmit} noValidate className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Lines */}
          <section className="lg:col-span-7">
            <h2 className="font-serif text-2xl text-[#252525] mb-4">{t("pages.order.varieties")}</h2>
            {cart.lines.length === 0 ? (
              <div className="border border-black/10 bg-white p-10 text-center" data-testid="order-empty">
                <p className="font-serif text-xl">{t("pages.order.emptyTitle")}</p>
                <Link to="/" className="inline-block mt-4 text-[12px] uppercase tracking-[0.14em] text-[#D92A47] border-b border-[#D92A47]">
                  {t("pages.variety.browse")}
                </Link>
              </div>
            ) : (
              <ul className="divide-y divide-black/10 bg-white border border-black/10" data-testid="order-lines">
                {cart.lines.map((l) => (
                  <li key={l.variety_id} className="p-4 flex gap-4 items-center">
                    <div className="h-16 w-16 bg-[#F6F2EC] overflow-hidden flex-shrink-0">
                      {l.image && <img src={resolveImageUrl(l.image)} alt={l.variety_name} className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg truncate">{l.variety_name}</div>
                      <div className="text-xs text-[#817B74]">{t("pages.order.unit")} <code>{l.unit}</code></div>
                    </div>
                    <input
                      type="number" min="1" step="1" value={l.quantity}
                      onChange={(e) => cart.updateLine(l.variety_id, { quantity: Number(e.target.value) || 1 })}
                      data-testid={`qty-${l.variety_id}`}
                      className="w-24 border border-black/15 px-3 py-2 text-right focus:outline-none focus:border-[#D92A47]"
                      aria-label={t("pages.order.quantity")}
                    />
                    <button type="button" onClick={() => cart.removeLine(l.variety_id)} data-testid={`remove-${l.variety_id}`}
                      className="grid place-items-center h-10 w-10 border border-black/10 hover:text-[#D92A47] hover:border-[#D92A47]"
                      aria-label={t("pages.order.removeLine")}>
                      <Trash2 size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {errors.lines && <p role="alert" className="text-[#D92A47] text-xs mt-2">{errors.lines}</p>}
          </section>

          {/* Contact */}
          <section className="lg:col-span-5">
            <h2 className="font-serif text-2xl text-[#252525] mb-4">{t("pages.order.contact")}</h2>
            <div className="space-y-5">
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-[#817B74] block mb-1">{t("form.contactName")} *</label>
                <input value={data.contact_name} onChange={set("contact_name")} className={fieldCls} data-testid="order-contact-name" aria-invalid={!!errors.contact_name} />
                {errors.contact_name && <p role="alert" className="text-[#D92A47] text-xs mt-1">{errors.contact_name}</p>}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-[#817B74] block mb-1">{t("form.email")} *</label>
                <input type="email" value={data.email} onChange={set("email")} className={fieldCls} data-testid="order-email" aria-invalid={!!errors.email} />
                {errors.email && <p role="alert" className="text-[#D92A47] text-xs mt-1">{errors.email}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-[#817B74] block mb-1">{t("form.company")}</label>
                  <input value={data.company} onChange={set("company")} className={fieldCls} data-testid="order-company" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-[#817B74] block mb-1">{t("form.country")}</label>
                  <input value={data.country} onChange={set("country")} className={fieldCls} data-testid="order-country" />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-[#817B74] block mb-1">{t("form.businessType")}</label>
                <select value={data.business_type} onChange={set("business_type")} className={fieldCls} data-testid="order-business-type">
                  <option value="">{t("pages.order.select")}</option>
                  {t("form.businessTypes").map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-[#817B74] block mb-1">{t("form.message")}</label>
                <textarea rows={3} value={data.message} onChange={set("message")} className={`${fieldCls} resize-none`} data-testid="order-message" />
              </div>
              <button type="submit" disabled={loading} data-testid="order-submit"
                className="inline-flex items-center justify-center gap-2 w-full h-14 px-6 bg-[#D92A47] text-[#F6F2EC] text-[13px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] transition-colors disabled:opacity-70">
                {loading ? (<><Loader2 size={16} className="animate-spin" /> {t("form.sending")}</>) : t("pages.order.submit")}
              </button>
              <p className="text-[11px] text-[#817B74] text-center">{t("pages.order.noPayments")}</p>
            </div>
          </section>
        </form>
      </div>
    </main>
  );
}
